import { BalanceData, EventReward, RARITY_IDS, chestShare } from "@highmarch/domain";
import { wellHtml } from "../components";
import { fmtCompact } from "../kit";
import { chestIconUrl, resourceIconUrl } from "../portraits";
import { essenceIconUrl, essenceWords, sigilIconUrl, sigilWords } from "../forgeArt";
import { fmtMinutes } from "../deals/dealsModel";
import { artUrl } from "../deals/dealsTiles";
import { RARITY_LABEL } from "../gearUi";
import { SKIN_LOOKS } from "../../render/skins/looks";
import { renderUrl } from "../hub/skinsModel";

/**
 * A pass reward as item wells (design system v2: a chest in its rarity's jewel well, everything else in the navy
 * well), and as words. Presentation only: what a reward is comes from seasons.json through @highmarch/app's season view.
 */

export interface PassPart {
  readonly art: string;
  readonly rarity: number | null;
  readonly count: string;
  readonly cap: string;
  readonly name: string;
}

/** A reward's parts, most notable first (a chest, sigils, gems, essence, a speed-up, gold). */
export function rewardParts(B: BalanceData, level: number, r: EventReward): PassPart[] {
  const parts: PassPart[] = [];
  if (r.chest !== null) parts.push({ art: chestIconUrl(RARITY_IDS[r.chest]), rarity: r.chest, count: "", cap: "", name: `${RARITY_LABEL[r.chest]} chest` });
  if (r.sigil !== null) parts.push({ art: sigilIconUrl(r.sigil), rarity: r.sigil, count: r.sigils > 1 ? `×${r.sigils}` : "", cap: "", name: sigilWords(r.sigil, Math.max(1, r.sigils)) });
  if (r.gems > 0) parts.push({ art: artUrl("hq:icons/res_gems.png"), rarity: null, count: fmtCompact(r.gems), cap: "", name: `${r.gems.toLocaleString("en-US")} gems` });
  if (r.essence > 0) parts.push({ art: essenceIconUrl(), rarity: null, count: fmtCompact(r.essence), cap: "", name: essenceWords(r.essence) });
  if (r.speedUpMinutes > 0) parts.push({ art: artUrl("hq:icons/res_speedup.png"), rarity: null, count: "", cap: fmtMinutes(r.speedUpMinutes), name: `${fmtMinutes(r.speedUpMinutes)} speed-up` });
  if (!r.resources.isZero()) {
    const gold = chestShare(B, Math.max(1, level), r.resources);
    parts.push({ art: resourceIconUrl("gold"), rarity: null, count: fmtCompact(gold), cap: "", name: `${gold.toLocaleString("en-US")} gold` });
  }
  return parts;
}

/** The season's skin as a part ("Border Warden tower skin"): its in-engine render (art/interface/skins); null for none. */
export function skinPart(skinId: string): PassPart | null {
  const look = skinId ? SKIN_LOOKS[skinId] : undefined;
  const art = look ? renderUrl(look) : null;
  return look && art ? { art, rarity: null, count: "", cap: "", name: `${look.name} ${skinId.split(".")[0]} skin` } : null;
}

/** A reward in words ("Epic chest · 60 gems"), with the cosmetic tier's skin and title. */
export function rewardText(B: BalanceData, level: number, r: EventReward, title = "", skin = ""): string {
  return [skinPart(skin)?.name ?? "", ...rewardParts(B, level, r).map((p) => p.name), title ? `the title “${title}”` : ""].filter(Boolean).join(" · ");
}

/** One part as a well (a skin's render gets the `pskin` light: ui/styles/season.css). */
export function partWell(p: PassPart, size: "sm" | "md" = "sm", className = "", dim = false): string {
  return wellHtml({ art: p.art, rarity: p.rarity, size, count: p.count, cap: p.cap, tag: "span", className, dim });
}
